import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { units } from '@/lib/format'

type Kind = 'success' | 'error' | 'info'
interface Toast {
  id: number
  kind: Kind
  text: string
}

interface ToastApi {
  push: (text: string, kind?: Kind) => void
  success: (text: string) => void
  error: (text: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

// Border + glyph colour, and a tint laid over an opaque surface: the soft fills are 10%
// alpha, so on their own the page underneath showed through the toast text.
const TONE: Record<Kind, string> = {
  success: 'border-ok-line text-ok',
  error: 'border-danger-line text-danger',
  info: 'border-hairline text-ink-1',
}
const TINT: Record<Kind, string> = {
  success: 'bg-ok-soft',
  error: 'bg-danger-soft',
  info: 'bg-surface-3',
}
const GLYPH: Record<Kind, string> = { success: '✓', error: '✕', info: '·' }

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const seq = useRef(0)

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const push = useCallback(
    (text: string, kind: Kind = 'info') => {
      const id = ++seq.current
      setToasts((t) => [...t, { id, kind, text }])
      setTimeout(() => remove(id), 4000)
    },
    [remove],
  )

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (t: string) => push(t, 'success'),
      error: (t: string) => push(t, 'error'),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-end gap-2 sm:left-auto print:hidden">
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => remove(t.id)}
            className={cn(
              'pointer-events-auto relative flex w-full max-w-sm items-center gap-2 overflow-hidden',
              'rounded-md border bg-surface-2 px-4 py-2 text-body shadow-e2 sm:w-auto',
              TONE[t.kind],
            )}
          >
            <span aria-hidden className={cn('absolute inset-0', TINT[t.kind])} />
            <span aria-hidden className="relative">
              {GLYPH[t.kind]}
            </span>
            {/* A server message may carry ⚡: the text unit, not the colour emoji (§6.14). */}
            <span className="relative min-w-0 text-left text-ink-1 [overflow-wrap:anywhere]">
              {units(t.text)}
            </span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (ctx === null) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

/** The toast API when there is a provider, else null — for shared states (ErrorState) that may
 *  render outside the app shell (a story, a static render in a test). */
export function useToastOptional(): ToastApi | null {
  return useContext(ToastContext)
}
